// JOBE-0054.10: composer-side upload primitives + chip rack.
//
// Three ingest paths (file picker, drag-drop, paste) all funnel into the
// same upload queue, where each entry uploads via POST /api/artifacts
// (JOBE-0054.3) using XMLHttpRequest so we can drive a real progress
// bar and abort on cancel. The fetch API still lacks a streaming-upload
// progress hook in mainstream browsers.
//
// Sending the message stitches each done-chip's URN onto the body text
// (V1 wire trade-off — channel-send carries `body: string` only;
// `MessageBody` detects the URN on the receive side and renders it as
// the existing ArtifactBadge → ArtifactPreview modal).

import { useCallback, useEffect, useRef, useState } from 'react';

// MAX_BYTES mirrors the server-side ARTIFACT_MAX_BYTES guard from
// JOBE-0054.3 so a too-large file fails on the client instead of after
// a long upload returns 413.
export const MAX_BYTES = 50 * 1024 * 1024;

// CANCEL_GRACE_MS is how long a `cancelled` chip stays visible after
// the user clicks × on an in-flight upload before it is auto-removed
// from the rack. The spec ("brief grace so the user sees the cancel
// landed") doesn't pin a number; 3s matches similar UX in the rest of
// the app and gives a beat to undo-by-eye without lingering.
export const CANCEL_GRACE_MS = 3000;

// URN_REGEX matches the canonical artifact URN shape
// `urn:jobe:artifact:<64-hex>` per JOBE-0054.5. Exported for the
// MessageBody receive-side renderer.
export const URN_REGEX = /urn:jobe:artifact:[0-9a-f]{64}/g;

// makeID generates a per-chip identifier scoped to the composer
// session. Not durable — chips don't outlive a successful send.
function makeID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// formatBytes renders a compact size string for the chip. Inlined here
// rather than shared with ArtifactPreview because the chip needs the
// short form (e.g. "12 KB") and the preview wants binary KiB/MiB
// suffixes for the metadata grid.
export function formatBytes(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '?';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// artifactIDToURN translates the operator's store-key shape
// (`sha256:<64-hex>`) into the canonical artifact URN
// (`urn:jobe:artifact:<64-hex>`). Returns '' for any unexpected shape
// so the caller can surface a "malformed upload response" error rather
// than persisting a bad value on the chip.
//
// Exported for tests.
export function artifactIDToURN(artifactID) {
  if (typeof artifactID !== 'string') return '';
  // Already a URN — pass through for forward-compat if the server
  // ever starts returning the canonical shape directly.
  if (/^urn:jobe:artifact:[0-9a-f]{64}$/.test(artifactID)) return artifactID;
  const m = /^sha256:([0-9a-f]{64})$/.exec(artifactID);
  if (!m) return '';
  return 'urn:jobe:artifact:' + m[1];
}

// uploadFile issues the multipart POST and reports lifecycle via the
// callbacks. The returned object exposes `abort()` so the chip's cancel
// button can stop an in-flight transfer. Exported for tests.
export function uploadFile(file, { onProgress, onDone, onError, xhrFactory } = {}) {
  const XHR = xhrFactory || (() => new XMLHttpRequest());
  const xhr = XHR();
  xhr.open('POST', '/api/artifacts', true);
  xhr.withCredentials = true;
  xhr.responseType = 'json';

  if (xhr.upload && typeof onProgress === 'function') {
    xhr.upload.onprogress = (ev) => {
      if (ev && ev.lengthComputable && ev.total > 0) {
        onProgress(ev.loaded / ev.total);
      }
    };
  }

  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      // jsdom doesn't always populate `response` for responseType=json;
      // fall back to parsing responseText.
      let body = xhr.response;
      if (!body && xhr.responseText) {
        try { body = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      }
      // Operator returns `artifact_id` in the store-key shape
      // `sha256:<hex>`. The composer + receive-side renderer key off
      // the canonical URN shape `urn:jobe:artifact:<hex>` (see
      // URN_REGEX), so translate here before storing on the chip /
      // stitching into the message body.
      const artifactID = body?.artifact_id || '';
      if (!artifactID) {
        onError?.(new Error('upload response missing artifact_id'));
        return;
      }
      const urn = artifactIDToURN(artifactID);
      if (!urn) {
        onError?.(new Error('upload response artifact_id has unexpected shape: ' + artifactID));
        return;
      }
      onDone?.({
        urn,
        mime_type: body.mime_type,
        size_bytes: body.size_bytes,
        deduped: !!body.deduped,
        // JOBE-0054.11: server-supplied expiry for orphan artifacts (no
        // `represents` rows). When present, the chip renders an
        // "expires <relative>" hint so the user knows the artifact will
        // be GC'd if they don't anchor it. The client MUST NOT compute
        // the TTL locally — the server is the source of truth.
        orphan_expires_at: body.orphan_expires_at || null,
      });
    } else {
      let detail = `HTTP ${xhr.status}`;
      const body = xhr.response || (() => {
        try { return JSON.parse(xhr.responseText || '{}'); } catch { return {}; }
      })();
      if (body?.message) detail = body.message;
      else if (body?.error) detail = body.error;
      onError?.(Object.assign(new Error(detail), { status: xhr.status }));
    }
  };

  xhr.onerror = () => onError?.(new Error('network error'));
  xhr.onabort = () => onError?.(Object.assign(new Error('cancelled'), { cancelled: true }));

  const fd = new FormData();
  fd.append('file', file, file.name || 'attachment');
  if (file.name) fd.append('original_filename', file.name);
  xhr.send(fd);

  return xhr;
}

// useAttachments owns the chip rack state. Returns the entries plus the
// callbacks the composer wires onto its file picker, drop target, and
// paste handler. `uploader` is an injection seam for tests; defaults to
// the real `uploadFile` above.
export function useAttachments({ uploader = uploadFile, cancelGraceMs = CANCEL_GRACE_MS } = {}) {
  const [entries, setEntries] = useState([]);
  // Keep the live XHRs out of state so React doesn't churn on every
  // progress tick. The ref is keyed by entry id.
  const xhrs = useRef(new Map());
  // Pending auto-remove timers for `cancelled` chips, keyed by entry id.
  // Tracked in a ref so the unmount-cleanup effect can clear them and
  // tests using fake timers can reason about them deterministically.
  const cancelTimers = useRef(new Map());

  const update = useCallback((id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const enqueue = useCallback((files) => {
    if (!files || files.length === 0) return;
    const newEntries = [];
    for (const file of files) {
      if (!file) continue;
      const id = makeID();
      // Size check up front so the user sees the error immediately
      // rather than after a long upload returns 413 server-side.
      if (typeof file.size === 'number' && file.size > MAX_BYTES) {
        newEntries.push({
          id,
          name: file.name || 'attachment',
          size: file.size,
          status: 'error',
          progress: 0,
          error: `too large (max ${formatBytes(MAX_BYTES)}) — try splitting`,
        });
        continue;
      }
      newEntries.push({
        id,
        name: file.name || 'attachment',
        size: typeof file.size === 'number' ? file.size : 0,
        status: 'uploading',
        progress: 0,
        file,
      });
    }
    if (newEntries.length === 0) return;
    setEntries((prev) => [...prev, ...newEntries]);
    for (const entry of newEntries) {
      if (entry.status !== 'uploading') continue;
      const xhr = uploader(entry.file, {
        onProgress: (p) => update(entry.id, { progress: p }),
        onDone: ({ urn, mime_type, size_bytes, orphan_expires_at }) => {
          xhrs.current.delete(entry.id);
          update(entry.id, {
            status: 'done',
            progress: 1,
            urn,
            mime_type,
            size: typeof size_bytes === 'number' ? size_bytes : entry.size,
            // JOBE-0054.11: persisted on the chip so the hint renders in
            // the `done` state. Absent / null = anchored artifact, no
            // hint shown.
            orphan_expires_at: orphan_expires_at || null,
            file: undefined,
          });
        },
        onError: (err) => {
          xhrs.current.delete(entry.id);
          if (err && err.cancelled) {
            // Cancelled entries already transitioned to `cancelled` in
            // `cancel`; the grace timer there handles removal.
            return;
          }
          update(entry.id, {
            status: 'error',
            error: (err && err.message) || 'upload failed',
            file: undefined,
          });
        },
      });
      xhrs.current.set(entry.id, xhr);
    }
  }, [uploader, update]);

  const cancel = useCallback((id) => {
    // User-initiated cancel of an in-flight upload. Per spec, the chip
    // transitions to `cancelled` and remains visible for a brief grace
    // so the user sees the cancel landed — distinct from `error` which
    // is reserved for unexpected failures.
    const xhr = xhrs.current.get(id);
    if (xhr && typeof xhr.abort === 'function') {
      try { xhr.abort(); } catch { /* swallow */ }
    }
    xhrs.current.delete(id);
    update(id, { status: 'cancelled', file: undefined });
    // Schedule auto-removal after the grace window. If another callsite
    // already armed a timer for this id (shouldn't happen, but be
    // defensive), clear it first so we never double-fire.
    const existing = cancelTimers.current.get(id);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      cancelTimers.current.delete(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }, cancelGraceMs);
    cancelTimers.current.set(id, t);
  }, [update, cancelGraceMs]);

  const remove = useCallback((id) => {
    // `remove` is the user dismissing a done / error / cancelled chip.
    // Cancel any in-flight transfer first so calling `remove` while
    // still uploading also stops the upload (matches user mental model:
    // clicking × on a chip kills it regardless of state). Also clear
    // any pending grace-period timer so it doesn't fire against a
    // stale id.
    const xhr = xhrs.current.get(id);
    if (xhr && typeof xhr.abort === 'function') {
      try { xhr.abort(); } catch { /* swallow */ }
    }
    xhrs.current.delete(id);
    const t = cancelTimers.current.get(id);
    if (t) {
      clearTimeout(t);
      cancelTimers.current.delete(id);
    }
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const retry = useCallback((id) => {
    // Re-enqueue with the same file. The entry is regenerated rather
    // than mutated in place so the chip's React key changes and any
    // animation restarts cleanly.
    setEntries((prev) => {
      const target = prev.find((e) => e.id === id);
      if (!target || !target.file) return prev;
      const next = prev.filter((e) => e.id !== id);
      // Defer the re-enqueue so it happens outside this setState batch.
      setTimeout(() => enqueue([target.file]), 0);
      return next;
    });
  }, [enqueue]);

  const clear = useCallback(() => {
    // Abort everything in flight before clearing — used on send so a
    // mid-flight upload from a previous draft doesn't leak into the
    // outbox entry.
    for (const xhr of xhrs.current.values()) {
      try { xhr.abort(); } catch { /* swallow */ }
    }
    xhrs.current.clear();
    for (const t of cancelTimers.current.values()) clearTimeout(t);
    cancelTimers.current.clear();
    setEntries([]);
  }, []);

  // Unmount cleanup: abort every in-flight XHR and clear pending
  // grace-period timers so a component teardown doesn't leak network
  // traffic or fire setState on an unmounted hook. Without this, an
  // upload kicked off in a `dm:` chat would keep streaming bytes after
  // the user navigates away — and any subsequent onload/onerror would
  // crash trying to setState on an unmounted hook.
  useEffect(() => {
    const xhrMap = xhrs.current;
    const timerMap = cancelTimers.current;
    return () => {
      for (const xhr of xhrMap.values()) {
        try { xhr.abort(); } catch { /* swallow */ }
      }
      xhrMap.clear();
      for (const t of timerMap.values()) clearTimeout(t);
      timerMap.clear();
    };
  }, []);

  return { entries, enqueue, cancel, remove, retry, clear };
}

// attachmentsToBodySuffix returns the trailing text appended to the
// outgoing message body. Exported for tests.
export function attachmentsToBodySuffix(entries) {
  const urns = entries
    .filter((e) => e.status === 'done' && e.urn)
    .map((e) => e.urn);
  if (urns.length === 0) return '';
  return urns.join('\n');
}

// composeBody joins draft text with attachment URNs. Returns '' when
// both halves are empty (caller treats '' as "nothing to send").
export function composeBody(draft, entries) {
  const text = (draft || '').trim();
  const suffix = attachmentsToBodySuffix(entries);
  if (!text && !suffix) return '';
  if (!text) return suffix;
  if (!suffix) return text;
  return `${text}\n\n${suffix}`;
}

// formatOrphanExpiresHint renders the server-supplied `orphan_expires_at`
// timestamp as a short relative string for the chip hint ("expires in
// 7 days", "expires today", "expires Friday"). The server is the source
// of truth — this function only formats; it never computes the TTL.
//
// Exported for tests. `now` is injectable so tests don't depend on the
// real clock.
export function formatOrphanExpiresHint(isoTimestamp, now = new Date()) {
  if (!isoTimestamp) return '';
  const expiry = new Date(isoTimestamp);
  if (Number.isNaN(expiry.getTime())) return '';
  const deltaMs = expiry.getTime() - now.getTime();
  if (deltaMs <= 0) return 'expires soon';
  const hourMs = 60 * 60 * 1000;
  const dayMs = 24 * hourMs;
  // Under a full day: render hours. Math.floor (not round) so 23h
  // doesn't snap up to "1 day" — the orphan-expiry hint is a worst-
  // case "will be GC'd after" affordance and must never overstate
  // remaining time. The exact-24h boundary tips into days territory.
  if (deltaMs < dayMs) {
    const hours = Math.max(1, Math.floor(deltaMs / hourMs));
    return hours === 1 ? 'expires in 1 hour' : `expires in ${hours} hours`;
  }
  const days = Math.floor(deltaMs / dayMs);
  if (days === 1) return 'expires in 1 day';
  return `expires in ${days} days`;
}

// AttachmentChip renders a single chip in the rack.
export function AttachmentChip({ entry, onCancel, onRemove, onRetry }) {
  const pct = Math.max(0, Math.min(100, Math.round((entry.progress || 0) * 100)));
  return (
    <div
      className={`attachment-chip attachment-chip-${entry.status}`}
      data-testid="attachment-chip"
      data-attachment-id={entry.id}
      data-attachment-status={entry.status}
    >
      <span className="attachment-chip-name mono" title={entry.name}>{entry.name}</span>
      {entry.size > 0 && (
        <span className="attachment-chip-size mono dim">{formatBytes(entry.size)}</span>
      )}
      {entry.status === 'uploading' && (
        <span className="attachment-chip-progress mono dim" data-testid="attachment-chip-progress">
          {pct}%
        </span>
      )}
      {entry.status === 'done' && entry.urn && (
        <span className="attachment-chip-urn mono dim" title={entry.urn}>
          {entry.urn.slice(-8)}
        </span>
      )}
      {entry.status === 'done' && entry.orphan_expires_at && (
        // JOBE-0054.11: server-supplied orphan-artifact expiry hint. Only
        // rendered for orphan artifacts (no `represents` rows on the
        // upload). Anchored artifacts never see this branch — they don't
        // carry `orphan_expires_at` in the response.
        <span
          className="attachment-chip-expires mono dim"
          data-testid="attachment-chip-expires"
          title={`Orphan artifact: will be GC'd after ${entry.orphan_expires_at} unless anchored to an entity.`}
        >
          {formatOrphanExpiresHint(entry.orphan_expires_at)}
        </span>
      )}
      {entry.status === 'error' && (
        <>
          <span className="attachment-chip-error mono" role="alert">{entry.error}</span>
          {entry.file && (
            <button
              type="button"
              className="attachment-chip-retry mono"
              data-testid="attachment-chip-retry"
              onClick={() => onRetry?.(entry.id)}
            >retry</button>
          )}
        </>
      )}
      {entry.status === 'cancelled' && (
        // User-initiated cancel — render a calmer "cancelled" label
        // (distinct from the red `attachment-chip-error` styling) so
        // the brief grace-period visibility reads as confirmation, not
        // failure. Auto-removed after CANCEL_GRACE_MS by the hook.
        <span className="attachment-chip-cancelled mono dim" data-testid="attachment-chip-cancelled">
          cancelled
        </span>
      )}
      <button
        type="button"
        className="attachment-chip-close"
        data-testid="attachment-chip-close"
        aria-label={entry.status === 'uploading' ? 'Cancel upload' : 'Remove attachment'}
        onClick={() => {
          if (entry.status === 'uploading') onCancel?.(entry.id);
          else onRemove?.(entry.id);
        }}
      >×</button>
    </div>
  );
}

// AttachmentRack is the row of chips above the textarea. Hidden when
// the entries array is empty so the composer keeps its usual height.
export function AttachmentRack({ entries, onCancel, onRemove, onRetry }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="attachment-rack" data-testid="attachment-rack">
      {entries.map((e) => (
        <AttachmentChip
          key={e.id}
          entry={e}
          onCancel={onCancel}
          onRemove={onRemove}
          onRetry={onRetry}
        />
      ))}
    </div>
  );
}

// filesFromDataTransfer extracts a File[] from a DragEvent's
// dataTransfer. Tolerates the `items` API as well as the older `files`
// list; both can appear depending on browser + drop source.
export function filesFromDataTransfer(dt) {
  if (!dt) return [];
  const out = [];
  if (dt.items && dt.items.length > 0) {
    for (const item of dt.items) {
      if (item.kind === 'file') {
        const f = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
        if (f) out.push(f);
      }
    }
    if (out.length > 0) return out;
  }
  if (dt.files && dt.files.length > 0) {
    for (const f of dt.files) out.push(f);
  }
  return out;
}

// filesFromClipboard extracts a File[] from a ClipboardEvent's
// clipboardData. Returns empty for plain-text pastes so the caller can
// let the default textarea behaviour run.
export function filesFromClipboard(cd) {
  if (!cd || !cd.items) return [];
  const out = [];
  for (const item of cd.items) {
    if (item.kind === 'file') {
      const f = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
      if (f) out.push(f);
    }
  }
  return out;
}
